import { environment } from "@app/relay/relay-environment";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { commitMutation, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { boardPage_Query } from "@app/queries/boardPage_Query.graphql";
import type { boardPage_GenerateBoardReportMutation } from "@app/queries/boardPage_GenerateBoardReportMutation.graphql";

export const boardQuery = graphql`
  query boardPage_Query {
    boardReport {
      generatedAt
      seats {
        role
        observations
        recommendation
        question
      }
    }
  }
`;

const generateMutation = graphql`
  mutation boardPage_GenerateBoardReportMutation {
    generateBoardReport {
      enqueued
      errors {
        message
      }
    }
  }
`;

const SEAT_STYLES: Record<string, string> = {
  CFO: "border-t-emerald-500",
  CMO: "border-t-blue-500",
  COO: "border-t-amber-500",
};

export default function BoardPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<boardPage_Query>;
}) {
  const data = usePreloadedQuery(boardQuery, queryRef);
  const router = useRouter();
  const [notice, setNotice] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const report = data.boardReport;

  const generate = () => {
    setGenerating(true);
    setNotice(null);

    commitMutation<boardPage_GenerateBoardReportMutation>(environment, {
      mutation: generateMutation,
      variables: {},
      onCompleted: (response) => {
        setGenerating(false);
        const payload = response.generateBoardReport;
        if (payload?.enqueued) {
          setNotice(
            "Board report is being generated — refresh this page in ~30 seconds."
          );
        } else {
          setNotice(
            payload?.errors?.[0]?.message ??
              "Could not enqueue the board report"
          );
        }
      },
      onError: (err) => {
        setGenerating(false);
        setNotice(err.message);
      },
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">AI board</h1>
          {report && (
            <p className="text-xs text-gray-400">
              Last meeting: {new Date(report.generatedAt).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {report && (
            <button
              type="button"
              onClick={() => router.invalidate()}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600"
            >
              Refresh
            </button>
          )}
          <button
            type="button"
            disabled={generating}
            onClick={generate}
            className="rounded bg-gray-900 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            {generating ? "Convening..." : "Hold board meeting"}
          </button>
        </div>
      </div>

      {notice && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded p-3">
          {notice}
        </p>
      )}

      {!report ? (
        <p className="text-gray-500 text-sm">
          No board report yet. "Hold board meeting" sends the current metrics
          (KPIs, P&L, MRR, unit economics, expense movers, data health) to three
          AI advisors — CFO, CMO, COO — who each return observations, one
          recommendation, and one question. Requires ANTHROPIC_API_KEY and a
          running Sidekiq worker.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {report.seats.map((seat) => (
            <div
              key={seat.role}
              className={`bg-white rounded border border-t-4 p-4 space-y-3 ${
                SEAT_STYLES[seat.role] ?? ""
              }`}
            >
              <h2 className="font-bold">{seat.role}</h2>

              <ul className="space-y-2 text-sm text-gray-700 list-disc pl-4">
                {seat.observations.map((observation, i) => (
                  <li key={i}>{observation}</li>
                ))}
              </ul>

              <div className="text-sm">
                <p className="text-xs font-semibold text-gray-400 uppercase">
                  Recommendation
                </p>
                <p className="text-gray-800 mt-1">{seat.recommendation}</p>
              </div>

              <div className="text-sm">
                <p className="text-xs font-semibold text-gray-400 uppercase">
                  Question for the founders
                </p>
                <p className="text-gray-800 italic mt-1">{seat.question}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
